import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Loader2, MailIcon } from 'lucide-react';
import { showToast } from '../../utils/toast';
import { verificationAPI } from '../../api/verification';
import { useAuth } from '../../contexts/AuthContext';

interface EmailVerificationProps {
  currentEmail?: string;
  onVerified: (email: string) => void;
  onCancel?: () => void;
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  currentEmail = '',
  onVerified,
  onCancel
}) => {
  const { user } = useAuth();
  const [email, setEmail] = useState(currentEmail || user?.email || '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [success, setSuccess] = useState(false);
  const [closeCountdown, setCloseCountdown] = useState(3);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (code.length === 6 && step === 'verify') {
      handleVerify();
    }
  }, [code, step]);

  useEffect(() => {
    if (success) {
      timerRef.current = window.setInterval(() => {
        setCloseCountdown(prev => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            onVerified(email);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [success, email, onVerified]);

  const handleCloseNow = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    onVerified(email);
  };

  const handleSendCode = async () => {
    if (!email.trim()) {
      showToast('Please enter an email address', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await verificationAPI.sendEmailVerification(email);
      if (response.data?.success) {
        showToast('Verification code sent to email', 'success');
        setStep('verify');
        setCountdown(60);
      } else {
        showToast(response.data?.error || 'Failed to send code', 'error');
      }
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Network error', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      showToast('Please enter 6-digit code', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await verificationAPI.verifyEmail(email, code);
      if (response.data?.success) {
        showToast('Email address verified!', 'success');
        setSuccess(true);
      } else {
        showToast(response.data?.error || 'Verification failed', 'error');
        if (response.data?.error?.includes('attempts')) {
          setAttemptsLeft(prev => prev - 1);
        }
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Network error';
      showToast(errorMsg, 'error');
      if (errorMsg.includes('attempts')) {
        setAttemptsLeft(prev => prev - 1);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    await handleSendCode();
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={() => success && handleCloseNow()}
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-bounce" />
            <h3 className="text-lg font-semibold text-gray-900">Email Verified!</h3>
            <p className="text-gray-600 mt-2">
              Closing in <span className="font-bold">{closeCountdown}</span> second{closeCountdown !== 1 ? 's' : ''}...
            </p>
            <button
              onClick={handleCloseNow}
              className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Close now
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              {/* <MessageCircle className="h-12 w-12 text-green-600 mx-auto mb-3" /> */}
              <MailIcon className="h-12 w-12 text-blue-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                {step === 'email' ? 'Verify Email Address' : 'Enter Verification Code'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {step === 'email'
                  ? 'We\'ll send a 6-digit code via email'
                  : `Code sent to ${email}`
                }
              </p>
            </div>

            {step === 'email' ? (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter your email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="you@example.com"
                />
                <div className="flex gap-3">
                  <button
                    onClick={handleSendCode}
                    disabled={isLoading || !email.trim()}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <MailIcon className="h-6 w-6 mr-2" />
                    )}
                    Send Email Code
                  </button>
                  {onCancel && (
                    <button
                      onClick={onCancel}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    6-Digit Code
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm text-center text-2xl tracking-widest"
                    maxLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Enter the code sent to your email
                  </p>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <button
                    onClick={() => setStep('email')}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    ← Change email address
                  </button>
                  <button
                    onClick={handleResend}
                    disabled={countdown > 0 || isLoading}
                    className="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  >
                    Resend code {countdown > 0 && `(${countdown}s)`}
                  </button>
                </div>

                {attemptsLeft < 5 && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      ⚠️ {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} left
                    </p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleVerify}
                    disabled={isLoading || code.length !== 6}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Verify Email Address
                  </button>
                  {onCancel && (
                    <button
                      onClick={onCancel}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};